define([
	'internal_util/Logger',
	'internal_util/Util',
	'internal_util/OpeneCardUtil',
	'internal_util/Configuration',
	'internal_util/ClientUtil',
	'model/Error',
	'model/ErrorType',
	'model/Context',
	'model/ActivationType',
	'service/CloudIdentitiesManager',
	'service/OptionsManager',
	'service/EidClientManager',
	'service/EventListenerRegistry',
	'service/SessionManager',
	'service/RecoveryStrategyManager',
	'service/AttributesManager',
	'amdi18n-loader?!service/nls/Core_strings'
], function (
	Logger,
	Util,
	OpeneCardUtil,
	Configuration,
	ClientUtil,
	Error,
	ErrorType,
	Context,
	ActivationType,
	CloudIdentitiesManager,
	OptionsManager,
	EidClientManager,
	EventListenerRegistry,
	SessionManager,
	RecoveryStrategyManager,
	AttributesManager,
	Strings
) {
	'use strict';

	/**
	 * Core class
	 */
	return function (start) {
		let _start,
			_parameters = null,
			_removedParameters = new Array(),
			_context = null,
			_cloudIdentitiesManager = null,
			_optionsManager = null,
			_eidClientManager = null,
			_recoveryStrategyManager = null,
			_attributesManager = null,
			_currentAuthFlowId = null,
			_originalAuthFlow = null,
			_originalContext = null;
		let that = this;

		if (typeof start === 'function') {
			_start = start;
		} else {
			throw new window.Error('invalid start parameter');
		}


		// private methods

		function log(method, message, data) {
			Logger.log('Core', method, message, data);
		}

		function callLog(method, message, data, event, callbacks) {
			log(method, message, data);
			EventListenerRegistry.callListeners(event, data, function () {
				Util.callCallbacks(callbacks, data);
			});
		}

		function removeParameter(parameterName) {
			let url = Util.currentURL(),
				key,
				i = 0;

			for (key in _parameters) {
				if (_parameters.hasOwnProperty(key)) {
					if (key !== parameterName && _removedParameters.indexOf(key) === -1) {
						if (i++ === 0) {
							url += '#';
						} else {
							url += '&';
						}

						url += key + '=' + encodeURIComponent(_parameters[key]);
					}
				}
			}

			_removedParameters.push(parameterName);

			ClientUtil.redirectTo(url);
		}

		function encodeRelayState(relayState) {
			return Util.base64Encode(JSON.stringify(relayState));
		}

		function decodeRelayState(relayState) {
			return JSON.parse(Util.base64Decode(relayState));
		}	

		function parseContext(callback) {
			let context;

			if (_parameters.hasOwnProperty('ctx')) {
				switch (_parameters["ctx"]) {
				case 'sp':
					context = Context.SERVICE_PROVIDER;
					callback(context);
					break;
				case 'idm':
					context = Context.CLOUD_IDENTITIES_MANAGEMENT;
					callback(context);
					break;
				case 'request_access':
					context = Context.CLOUD_IDENTITIES_API_REQUEST;
					callback(context);
					break;
				case 'finish':
					if (_parameters.hasOwnProperty('relay-state')) {
						let relayState = decodeRelayState(_parameters['relay-state']);
						_originalAuthFlow = relayState['originalAuthFlow'];
						_originalContext = relayState['originalContext']
					}

					if(_parameters.hasOwnProperty('flow-id')) {
						let flowId = _parameters['flow-id']

						_optionsManager = new OptionsManager(flowId)
						_optionsManager.finish(function(brokerFinishResp) {
							switch (brokerFinishResp["fsProtocol"]) {
								case "CLOUDID_ISSUANCE":
									// switch to IDM context with the issue cmd
									context = Context.CLOUD_IDENTITIES_MANAGEMENT;
									_parameters['ctx'] = 'idm';

									if (brokerFinishResp.hasOwnProperty("authResultError") && brokerFinishResp["authResultError"] != null) {
										let idmStart = Util.currentURL() + '#ctx=idm';
										ClientUtil.redirectTo(idmStart);
										callback(context);
									} else {
										_parameters["cip-cmd"] = 'issue';
										_parameters["flow-id"] = flowId;

										let issueUri = Util.currentURL() + '#ctx=idm&cip-cmd=issue';
										ClientUtil.redirectTo(issueUri);
										callback(context);
									}

									break;
								case "CLOUDID_REVOCATION":
									// switch to IDM context with the revoke-all cmd
									context = Context.CLOUD_IDENTITIES_MANAGEMENT;
									_parameters['ctx'] = 'idm';
									_parameters["cip-cmd"] = 'revoke-all';
									_parameters["flow-id"] = flowId;

									let revokeAllUri = Util.currentURL() + '#ctx=idm&cip-cmd=revoke-all';
									ClientUtil.redirectTo(revokeAllUri);
									callback(context);
									break;
								case "WEB_REDIRECT":
									// we don't care about the context and switch to the FS
									ClientUtil.redirectTo(brokerFinishResp["fsUrl"]);
									break;
							}
						});
					}
					break;
				default:
					throw new window.Error(Strings.invalid_context + context);
				}
			} else if (_parameters.hasOwnProperty('broker-token')) {
				context = Context.SERVICE_PROVIDER;

				_parameters['ctx'] = 'sp';

				ClientUtil.redirectTo(Util.currentURL() + '#ctx=sp&broker-token=' + _parameters['broker-token']);
				callback(context);
			} else {
				if (Util.currentURL().endsWith("request_access.html")) {
					context = Context.CLOUD_IDENTITIES_API_REQUEST;

					_parameters['ctx'] = 'request_access';

					ClientUtil.redirectTo(Util.currentURL() + '#ctx=request_access');
					callback(context);
				} else {
					context = Context.CLOUD_IDENTITIES_MANAGEMENT;

					_parameters['ctx'] = 'idm';

					ClientUtil.redirectTo(Util.currentURL() + '#ctx=idm');
					callback(context);
				}
			}
		}

		function validateContext(context) {
			switch (context) {
				case Context.SERVICE_PROVIDER:
					if (! _parameters.hasOwnProperty('broker-token') && ! _parameters.hasOwnProperty('flow-id')) {
						throw new window.Error(Strings.no_session_found + window.location.href);
					}
					break;
				case Context.CLOUD_IDENTITIES_MANAGEMENT:
					if (_parameters.hasOwnProperty('cip-cmd')) {
						if (_parameters['cip-cmd'] !== 'issue' && _parameters['cip-cmd'] !== 'revoke-all') {
							throw new window.Error(Strings.unknown_cip_command + _parameters['cip-cmd']);
						}
						removeParameter("cip-cmd");
					}

					break;
			}
			return context;
		}

		function selectAndActivate(optionsManager, authenticationOption, requestedAttributes, cloudIdentity, binding, secret) {
			let _name = 'selectAndActivate';

			callLog(_name, Strings.activation_started, authenticationOption, 'ACTIVATION_STARTED', function () {
				optionsManager.selectOption(authenticationOption, requestedAttributes, function (activationURL) {
					switch (authenticationOption.activation()) {
					case ActivationType.BROWSER:
						callLog(_name, Strings.activation_succeeded, authenticationOption, 'ACTIVATION_SUCCEEDED', function () {
							ClientUtil.redirectTo(activationURL);
						});
						break;
					case ActivationType.EID_CLIENT:
						_eidClientManager.activateEidClient(authenticationOption, activationURL, function () {
							// activation succeeded: nothing to do
						}, function () {
							callLog(_name, Strings.activation_failed, authenticationOption, 'ACTIVATION_FAILED');
						});
						break;
					case ActivationType.BROWSER_XHR:
						// currently only cloudids are supported for this activation type
						if (authenticationOption.cloudID()) {
							_cloudIdentitiesManager.activateCloudIdentity(cloudIdentity, binding, secret, activationURL, function (redirectURL) {
								callLog(_name, Strings.activation_succeeded, authenticationOption, 'ACTIVATION_SUCCEEDED', function () {
									ClientUtil.redirectTo(redirectURL);
								});
							}, function (error) {
								callLog(_name, Strings.activation_failed, error, 'ACTIVATION_FAILED');
							});
						} else {
							callLog(_name, Strings.activation_failed_type_browser_xhr_unsupported_for_option, authenticationOption, 'ACTIVATION_FAILED');
						}
						break;
					default:
						callLog(_name, Strings.activation_failed_type_unknown, authenticationOption, 'ACTIVATION_FAILED');
					}
				});
			});
		}
		
		function abort() {
			callLog('abort', Strings.abort, null, 'ABORT', function () {
				if (ClientUtil.isWebApp()) {
					window.close();
				} else if (_optionsManager === null && ! _parameters.hasOwnProperty('broker-token')) {
					window.history.go(-1);
				} else {
					if (_optionsManager === null) {
						SessionManager.claimSession(_parameters['broker-token'], function(authFlowId) {
							_currentAuthFlowId = authFlowId;
							_optionsManager = new OptionsManager(authFlowId);
						});
					}

					_optionsManager.cancelSelectOption(function (rawRedirectURL) {
						let redirectUrl = new URL(rawRedirectURL);
						
						if (_context == Context.CLOUD_IDENTITIES_MANAGEMENT && 
								redirectUrl.hostname == window.location.hostname && 
								redirectUrl.pathname == window.location.pathname) {
							// we're already at the finish location in IdS in IDM context
							// so we just cancel the current auth flow and restart the IdS
							let idsStartUrl = Configuration.get("IDM_URL");
							ClientUtil.redirectTo(idsStartUrl);
						} else {
							ClientUtil.redirectTo(rawRedirectURL);
						}
					});
				}
			});
		}


		// public methods

		this.parameters = function () {
			return _parameters;
		};

		this.cloudIdentitiesManager = function () {
			return _cloudIdentitiesManager;
		};

		this.optionsManager = function () {
			return _optionsManager;
		};

		this.eidClientManager = function () {
			return _eidClientManager;
		};

		this.recoveryStrategyManager = function () {
			return _recoveryStrategyManager;
		};

		this.attributesManager = function () {
			return _attributesManager;
		};

		this.context = function () {
			return _context;
		};

		this.contextChangeable = function () {
			if (_originalAuthFlow != null && _originalContext != null) {
				return true;
			} else {
				// if we're are in SP context with a broker-token, we're able to switch
				// to IDM context
				return _parameters.hasOwnProperty('broker-token');
			}
		};

		this.changeContext = function () {
			let _name = 'changeContext';

			callLog(_name, Strings.context_change_started, _context, 'CONTEXT_CHANGE_STARTED', function () {
				switch (_context) {
				case Context.SERVICE_PROVIDER:
					_context = Context.CLOUD_IDENTITIES_MANAGEMENT;
					_originalAuthFlow = _currentAuthFlowId;
					_originalContext = "sp";

					ClientUtil.redirectTo(Util.currentURL() + '#ctx=idm&flow-id=' + _currentAuthFlowId);
					break;
				case Context.CLOUD_IDENTITIES_MANAGEMENT:
					_context = Context.SERVICE_PROVIDER;

					ClientUtil.redirectTo(Configuration.get("IDM_URL") + '#ctx=sp&flow-id=' + _originalAuthFlow);
					break;
				}

				callLog(_name, Strings.context_change_succeeded, _context, 'CONTEXT_CHANGE_SUCCEEDED', function () {
					Util.callCallbacks(start);
				});
			});
		};

		this.init = function (callbacks, errorCallbacks) {
			let _name = 'init',
				error;

			callLog(_name, Strings.initialization_started, null, 'CORE_INITIALIZATION_STARTED');

			if (! ClientUtil.isSupportedBrowser()) {
				error = new Error(ErrorType.NON_RECOVERABLE, Strings.initialization_failed, Strings.browser_not_supported, true);
				callLog(_name, Strings.initialization_failed, error, 'CORE_INITIALIZATION_FAILED', errorCallbacks);
				return;
			}

			Configuration.checkConfigAsync(function (configuration) {
				log(_name, Strings.version + configuration['VERSION']);

				_parameters = Util.parseParameters();

				parseContext(function(context) {
					try {
						_context = validateContext(context);
					} catch (error) {
						error = new Error(ErrorType.NON_RECOVERABLE, Strings.initialization_failed, error.message, true);
						callLog(_name, Strings.initialization_failed, error, 'CORE_INITIALIZATION_FAILED', errorCallbacks);
						return;
					}

					if(_parameters.hasOwnProperty('broker-token')) {
						SessionManager.claimSession(_parameters['broker-token'], function(authFlowId) {
							_currentAuthFlowId = authFlowId;
							_optionsManager = new OptionsManager(authFlowId);
						});
					} else if (_parameters.hasOwnProperty('flow-id')) {
						_currentAuthFlowId = _parameters['flow-id'];
						_optionsManager = new OptionsManager(_currentAuthFlowId);
					}

					_attributesManager = new AttributesManager();
					_cloudIdentitiesManager = new CloudIdentitiesManager();
					_eidClientManager = new EidClientManager();
					_recoveryStrategyManager = new RecoveryStrategyManager(_eidClientManager);
	
					callLog(_name, Strings.initialization_succeeded, _context, 'CORE_INITIALIZATION_SUCCEEDED', callbacks);
				});
			}, function (error) {
				callLog(_name, Strings.initialization_failed, error, 'CORE_INITIALIZATION_FAILED', errorCallbacks);
			});
		};

		this.start = function (callbacks, errorCallbacks) {
			let _name = 'start';

			callLog(_name, Strings.started, null, 'CORE_STARTED');

			_attributesManager.getAttributeDefinitions(function () {
				_cloudIdentitiesManager.getCloudIdentityOptions(function () {
					OpeneCardUtil.fetchSupportedCardsProtocols(function () {
						_cloudIdentitiesManager.init();

						switch (_context) {
							case Context.CLOUD_IDENTITIES_API_REQUEST:
								callLog(_name, Strings.succeeded, null, 'CORE_SUCCEEDED', callbacks);
								break;
							case Context.SERVICE_PROVIDER:
								_optionsManager.getOptions(function () {
									callLog(_name, Strings.succeeded, null, 'CORE_SUCCEEDED', callbacks);
								});
								break;
							case Context.CLOUD_IDENTITIES_MANAGEMENT:
								if (_parameters.hasOwnProperty('cip-cmd')) {
									switch (_parameters['cip-cmd']) {
										//deligate issue
										case 'issue':
											_optionsManager.getOptions(function () {
												callLog(_name, Strings.succeeded, null, 'CORE_SUCCEEDED', callbacks);
											});
											break;
										case 'revoke-all':
											_optionsManager.getOptions(function () {
												_cloudIdentitiesManager.revokeCloudIdentities(_parameters['flow-id'], function () {
													callLog(_name, Strings.succeeded, null, 'CORE_SUCCEEDED', callbacks);
												});
											});
											break;
										default:
											if(_parameters.hasOwnProperty('broker-token')) {
												_optionsManager.getOptions(function () {
													callLog(_name, Strings.unknown_cip_command, null, 'CORE_SUCCEEDED', callbacks);
												});
											} else {
												callLog(_name, Strings.unknown_cip_command, null, 'CORE_SUCCEEDED', callbacks);
											}
									}
								} else {
									if(_parameters.hasOwnProperty('flow-id')) {
										_optionsManager.getOptions(function () {
											callLog(_name, Strings.succeeded, null, 'CORE_SUCCEEDED', callbacks);
										});
									} else {
										callLog(_name, Strings.succeeded, null, 'CORE_SUCCEEDED', callbacks);
									}
								}
						}
					});
				});
			});
		};
		
		this.abort = function () {
			abort();
		};

		this.activate = function (authenticationOption, requestedAttributes, cloudIdentity, binding, secret) {
			selectAndActivate(_optionsManager, authenticationOption, requestedAttributes, cloudIdentity, binding, secret);
		};

		this.createCloudIdentity = function (cipCmd, authenticationOption, requestedAttributes) {
			// create AuthRequest to send to CloudID Producer
			let authRequest = {
				AuthenticationOptions: [],
				RequestedAttributes: [],
				CIPCmd: cipCmd
			};
			
			// create relay state
			let relayState = Util.parseParameters(window.location.hash, false);
			if (_originalAuthFlow != null & _originalContext != null) {
				relayState['originalAuthFlow'] = _originalAuthFlow;
				relayState['originalContext'] = _originalContext;
			}
			relayState = encodeRelayState(relayState);
			
			_cloudIdentitiesManager.cloudIdentityRequest(authRequest, relayState, function (brokerToken) {
				SessionManager.claimSession(brokerToken, function(authFlowId) {
					_currentAuthFlowId = authFlowId;
					selectAndActivate(new OptionsManager(authFlowId), authenticationOption, requestedAttributes);
				});
			});
		};

		this.revokeCloudIdentities = function (authenticationOption, requestedAttributes) {
			// create AuthRequest to send to CloudID Producer
			let authRequest = {
				AuthenticationOptions: [],
				RequestedAttributes: [],
				CIPCmd: 'revoke-all'
			};
			
			_cloudIdentitiesManager.cloudIdentityRequest(authRequest, null, function (brokerToken) {
				SessionManager.claimSession(brokerToken, function(authFlowId) {
					_currentAuthFlowId = authFlowId;
					selectAndActivate(new OptionsManager(authFlowId), authenticationOption, requestedAttributes);
				});
			});
		};

		this.abortActivate = function () {
			try {
				window.stop();
			} catch (e) {
				document.execCommand('Stop');
			}

			_recoveryStrategyManager.stopRecoveryStrategies();
		};
	};
});



//////////////////
// WEBPACK FOOTER
// ./src/main/web/js/app/service/Core.js
// module id = 42
// module chunks = 4